import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export interface APILog {
  id: string;
  workspace_id: string;
  integration_config_id: string;
  api_endpoint: string;
  http_method: string;
  request_payload: any;
  http_status_code: number;
  response_payload: any;
  auth_method: string;
  auth_status: string;
  processing_duration_ms: number;
  was_successful: boolean;
  error_message: string | null;
  error_type: string | null;
  used_fallback: boolean;
  fallback_provider: string | null;
  created_at: string;
}

export const useAPILogs = (limit: number = 50) => {
  const workspaceContext = useWorkspace();
  const workspace = workspaceContext?.currentWorkspace;

  return useQuery({
    queryKey: ['parser-api-logs', workspace?.id, limit],
    queryFn: async () => {
      if (!workspace?.id) return [];

      const { data, error } = await supabase
        .from('parser_api_logs')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as APILog[];
    },
    enabled: !!workspace?.id,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
};

export const useAPILogsByStatus = (status: 'success' | 'failed' | 'auth_required', limit: number = 20) => {
  const workspaceContext = useWorkspace();
  const workspace = workspaceContext?.currentWorkspace;

  return useQuery({
    queryKey: ['parser-api-logs-by-status', workspace?.id, status, limit],
    queryFn: async () => {
      if (!workspace?.id) return [];

      let query = supabase
        .from('parser_api_logs')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (status === 'success') {
        query = query.eq('was_successful', true);
      } else if (status === 'failed') {
        query = query.eq('was_successful', false);
      } else if (status === 'auth_required') {
        query = query.eq('auth_status', 'failed');
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as APILog[];
    },
    enabled: !!workspace?.id,
  });
};
